/**
 * Events → Trends ("מגמות זמן") — weekly time-series view.
 *
 * Composes the live `/v1/contract` slices (`trends`, `campaign_trend`,
 * `week_labels`, `districts`) with the honesty primitives and a
 * minimal multi-line chart.
 */
import { useMemo, useState } from 'react';
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsTrends } from '../../../data/queries/useEventsTrends';
import { TimeSeries, type TimeSeriesPoint } from '../../../components/charts/TimeSeries';
import { AnomalyChip, Denominator, Freshness } from '../../../components/honesty';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber } from '../../../utils/format';
import type { TrendDistrict, TrendsBundle } from '../../../data/api/events';

export interface TrendsViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

/* A small fixed palette mirroring the prototype's TrendsView. */
const PALETTE = [
  'var(--c-navy, #0b1220)',
  'var(--c-blue, #1d4ed8)',
  'var(--c-indigo, #3730a3)',
  'var(--accent, #0f7a48)',
  'var(--c-violet, #6d28d9)',
  'var(--c-slate, #475569)',
  'var(--c-steel, #334155)',
];

export function TrendsView({ system, module }: TrendsViewProps) {
  const { data, isLoading, error } = useEventsTrends();

  return (
    <article className="module-view trends-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'מגמות זמן'}</span>
        </div>
        <h1>{module?.he ?? 'מגמות זמן'}</h1>
        <p className="mp-sub">
          {module?.sub ?? 'סדרת זמן שבועית, פיצול לפי מחוז מול הקצב הארצי.'}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען נתוני מגמה…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <TrendsBody envelope={data} />}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Body — KPIs, chart, classifier table, footer.                      */
/* ------------------------------------------------------------------ */

interface TrendsBodyProps {
  envelope: TrendsBundle;
}

function districtKey(d: TrendDistrict): string {
  return d.id ?? d.he ?? '—';
}

function districtLabel(d: TrendDistrict): string {
  return d.he ?? d.id ?? '—';
}

function TrendsBody({ envelope }: TrendsBodyProps) {
  const { trends, campaign_trend, week_labels, districts, meta } = envelope;

  // Districts the chart can show — every district that has a series
  // entry in `trends`. Fall back to the keys in `trends` if `districts`
  // is empty (defensive: the contract should always carry both).
  const chartable = useMemo<TrendDistrict[]>(() => {
    if (districts.length > 0) {
      return districts.filter((d) => {
        const k = d.id ?? d.he;
        return k ? Array.isArray(trends[k]) : false;
      });
    }
    return Object.keys(trends).map((name) => ({ id: name, he: name }));
  }, [districts, trends]);

  // Default selection — top three by latest-week attendance so the
  // chart isn't empty on first paint.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => {
    const sorted = [...chartable].sort((a, b) => {
      const aPts = trends[a.id ?? a.he ?? ''] ?? [];
      const bPts = trends[b.id ?? b.he ?? ''] ?? [];
      return (bPts[bPts.length - 1] ?? 0) - (aPts[aPts.length - 1] ?? 0);
    });
    return new Set(sorted.slice(0, 3).map(districtKey));
  });

  function toggle(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /* -- KPI math ------------------------------------------------------ */
  const weeks = campaign_trend.length;
  const latest = weeks > 0 ? campaign_trend[weeks - 1] ?? 0 : 0;
  const previous = weeks > 1 ? campaign_trend[weeks - 2] ?? 0 : 0;
  const delta = latest - previous;
  const deltaPct = previous > 0 ? delta / previous : 0;

  const accelerating = districts.filter((d) => d.trend === 'accelerating').length;
  const stalling = districts.filter((d) => d.trend === 'stalling').length;

  /* -- Chart series -------------------------------------------------- */
  const chartSeries: TimeSeriesPoint[] = useMemo(() => {
    const out: TimeSeriesPoint[] = [
      {
        id: '__campaign',
        label: 'ארצי',
        points: campaign_trend,
        color: 'var(--ink, #0b1220)',
        emphasized: true,
        dashed: false,
      },
    ];
    let colourIdx = 0;
    for (const d of chartable) {
      const key = districtKey(d);
      if (!selectedKeys.has(key)) continue;
      const pts = trends[d.id ?? d.he ?? ''] ?? [];
      out.push({
        id: key,
        label: districtLabel(d),
        points: pts,
        color: PALETTE[colourIdx % PALETTE.length],
        trend: d.trend,
      });
      colourIdx += 1;
    }
    return out;
  }, [campaign_trend, chartable, selectedKeys, trends]);

  return (
    <>
      {/* KPI strip — four-up to mirror the funnel view. */}
      <div className="kpi-grid cols-4">
        <KpiCell
          label="הגעות שב׳ אחרון"
          value={formatNumber(latest)}
          sub={`ארצי · ${week_labels[weeks - 1] ?? '—'}`}
          accent
        />
        <KpiCell
          label="שינוי שב׳ מול שב׳"
          value={`${delta >= 0 ? '+' : '−'}${formatNumber(Math.abs(delta))}`}
          sub={
            previous > 0
              ? `${delta >= 0 ? '+' : '−'}${Math.round(Math.abs(deltaPct) * 100)}% מהשבוע הקודם`
              : 'אין בסיס השוואה'
          }
          focus
        />
        <KpiCell
          label="מחוזות בהאצה"
          value={String(accelerating)}
          sub={`מתוך ${districts.length} מחוזות`}
        />
        <KpiCell
          label="מחוזות בהאטה"
          value={String(stalling)}
          sub={stalling > 0 ? 'דורש בדיקה' : 'אין התראות'}
          warn={stalling > 0}
        />
      </div>

      {/* Main panel — time-series chart + legend toggles. */}
      <div className="panel">
        <div className="panel-head">
          <div className="title">קצב הגעות שבועי</div>
          <div className="sub">
            הקו השחור הוא הקצב הארצי. בחר מחוזות להשוואה.
          </div>
        </div>

        <div
          className="trend-toolbar"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
            padding: '8px 22px 0',
          }}
        >
          <span
            className="label"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              color: 'var(--ink-3)',
              marginInlineEnd: 8,
            }}
          >
            מחוזות להשוואה
          </span>
          {chartable.map((d) => {
            const key = districtKey(d);
            const active = selectedKeys.has(key);
            return (
              <button
                key={key}
                type="button"
                className={'chip' + (active ? ' active' : '')}
                onClick={() => toggle(key)}
                aria-pressed={active}
              >
                {districtLabel(d)}
              </button>
            );
          })}
        </div>

        <div style={{ padding: '12px 22px 24px' }}>
          <ChartLegend series={chartSeries} />
          <TimeSeries
            series={chartSeries}
            xLabels={week_labels}
            height={320}
          />
        </div>
      </div>

      {/* Secondary panel — classifier table per district. */}
      {districts.length > 0 && (
        <ClassifierTable rows={districts} trends={trends} />
      )}

      {/* Honesty footer. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator
          n={districts.length}
          total={districts.length}
          label="מחוזות פעילים"
        />
        <Freshness updatedAt={toEpoch(envelope.source_pulled_at)} />
        <span style={{ color: 'var(--ink-3)' }}>
          מסווג מגמה: ממוצע 4 שב׳ אחרונות מול 4 שב׳ קודמות
        </span>
        {meta.anomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={a.severity === 'critical' ? 'alert' : a.severity}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Local presentational helpers                                        */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
  warn?: boolean;
}

function KpiCell({ label, value, sub, accent, focus, warn }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus', warn && 'warn']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}

interface ChartLegendProps {
  series: TimeSeriesPoint[];
}

function ChartLegend({ series }: ChartLegendProps) {
  return (
    <div
      className="chart-legend"
      style={{
        display: 'flex',
        gap: 16,
        fontSize: 11,
        color: 'var(--ink-3)',
        marginBottom: 12,
        flexWrap: 'wrap',
      }}
    >
      {series.map((s) => (
        <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span
            style={{
              display: 'inline-block',
              width: 14,
              height: s.emphasized ? 2 : 1.5,
              background: s.color ?? 'var(--ink)',
              marginInlineEnd: 6,
              verticalAlign: 'middle',
            }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

interface ClassifierTableProps {
  rows: TrendDistrict[];
  trends: TrendsBundle['trends'];
}

/**
 * Tiny-baseline cap for the displayed change %. Anything beyond ±999%
 * is mathematically real but operationally meaningless — almost always
 * a near-zero baseline artifact (e.g. שפלה going from 1 → 339 reads as
 * +33,800%, which is technically correct but actively misleading).
 */
const CHANGE_DISPLAY_CAP_PCT = 999;

/**
 * "Insufficient data" guard for the classifier badge. If BOTH the
 * last week's value AND the prior 4-week sum are below this floor, the
 * district doesn't have enough activity to defensibly call it
 * accelerating or stalling. The server-side classifier should ideally
 * apply the same rule — until it does, we override at render time.
 */
const INSUFFICIENT_ACTIVITY_FLOOR = 5;

function ClassifierTable({ rows, trends }: ClassifierTableProps) {
  const sorted = useMemo(() => {
    const order: Record<string, number> = {
      accelerating: 0,
      stable: 1,
      stalling: 2,
    };
    return [...rows].sort(
      (a, b) =>
        (order[a.trend ?? 'stable'] ?? 1) - (order[b.trend ?? 'stable'] ?? 1),
    );
  }, [rows]);

  const numHeader: React.CSSProperties = { textAlign: 'right' };
  const noBaselineTooltip =
    'הבסיס (ממוצע 4 השבועות הקודמים) היה אפס או קרוב לאפס — אחוז השינוי אינו משמעותי בהקשר זה.';

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="title">סיווג מגמה לפי מחוז</div>
        <div className="sub">השוואה ל-4 השבועות הקודמים</div>
      </div>
      <div
        style={{
          padding: '8px 22px 0',
          fontFamily: 'var(--font-sans)',
          fontSize: 11.5,
          color: 'var(--ink-3)',
          lineHeight: 1.5,
        }}
      >
        סיווג מגמה מבוסס על השוואת ההגעות בשבוע האחרון לממוצע 4 השבועות
        הקודמים. מחוזות עם פעילות נמוכה מסומנים &quot;חסר נתונים&quot;.
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>מחוז</th>
            <th style={numHeader}>הגעות שב׳ אחרון</th>
            <th style={numHeader}>שינוי 4 שב׳</th>
            <th>מצב</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => {
            const key = districtKey(d);
            const points = trends[d.id ?? d.he ?? ''] ?? [];
            const last = points[points.length - 1] ?? 0;
            // The classifier window is "last 4 vs prev 4" — express it as
            // a single human-readable delta % so the row tells the same
            // story the trend tag implies.
            const len = points.length;
            const window4 = points.slice(Math.max(0, len - 4));
            const prev4 = points.slice(Math.max(0, len - 8), Math.max(0, len - 4));
            const avgRecent = window4.length
              ? window4.reduce((a, b) => a + b, 0) / window4.length
              : 0;
            const avgPrev = prev4.length
              ? prev4.reduce((a, b) => a + b, 0) / prev4.length
              : 0;
            const prev4Total = prev4.reduce((a, b) => a + b, 0);
            const change = avgPrev > 0 ? (avgRecent - avgPrev) / avgPrev : 0;

            // Cap the displayed change at ±CHANGE_DISPLAY_CAP_PCT to
            // avoid the "+33800%" tiny-baseline noise.
            const changePct = Math.round(Math.abs(change) * 100);
            const capped = changePct > CHANGE_DISPLAY_CAP_PCT;
            const displayPct = capped ? CHANGE_DISPLAY_CAP_PCT : changePct;
            const sign = change >= 0 ? '+' : '−';
            const cappedSuffix = capped ? (change >= 0 ? '↑' : '↓') : '';
            const changeCell =
              avgPrev === 0
                ? '—'
                : `${sign}${displayPct}%${cappedSuffix}`;
            const changeTitle =
              avgPrev === 0
                ? noBaselineTooltip
                : capped
                  ? `שינוי בפועל ≈ ${sign}${changePct}% — מוצג כ-${sign}${CHANGE_DISPLAY_CAP_PCT}%${cappedSuffix} כי הבסיס היה קטן מאוד.`
                  : '';

            // Promote to "חסר נתונים" when both the latest week and
            // the prior 4-week window are below the activity floor.
            const insufficient =
              last < INSUFFICIENT_ACTIVITY_FLOOR &&
              prev4Total < INSUFFICIENT_ACTIVITY_FLOOR;
            const effectiveTrend = insufficient ? 'insufficient' : d.trend;

            return (
              <tr key={key}>
                <td><b>{districtLabel(d)}</b></td>
                <td className="num-cell mono">{formatNumber(last)}</td>
                <td
                  className="num-cell mono"
                  title={changeTitle || undefined}
                  style={{
                    color:
                      avgPrev === 0
                        ? 'var(--ink-3)'
                        : change > 0
                          ? 'var(--positive, #0f7a48)'
                          : change < 0
                            ? 'var(--negative, #b91c1c)'
                            : 'var(--ink-3)',
                  }}
                >
                  {changeCell}
                </td>
                <td>
                  <TrendBadge trend={effectiveTrend} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div
        style={{
          padding: '8px 22px 12px',
          fontFamily: 'var(--font-sans)',
          fontSize: 11,
          color: 'var(--ink-3)',
          lineHeight: 1.5,
        }}
      >
        — = אין בסיס השוואה מספק (פעילות נמוכה מדי בתקופה הקודמת).
      </div>
    </div>
  );
}

interface TrendBadgeProps {
  trend?: TrendDistrict['trend'] | 'insufficient';
}

function TrendBadge({ trend }: TrendBadgeProps) {
  const cfg =
    trend === 'accelerating'
      ? { label: 'מאיץ', bg: 'var(--positive-soft, #d1fae5)', fg: 'var(--positive, #0f7a48)' }
      : trend === 'stalling'
        ? { label: 'מאט', bg: 'var(--warning-soft, #fef3c7)', fg: 'var(--warning, #b45309)' }
        : trend === 'insufficient'
          ? { label: 'חסר נתונים', bg: 'var(--paper-2, #f1f1ee)', fg: 'var(--ink-3, #6b7280)' }
          : { label: 'יציב', bg: 'var(--paper-2, #f1f1ee)', fg: 'var(--ink-3, #6b7280)' };
  return (
    <span
      style={{
        fontSize: 11,
        padding: '2px 8px',
        background: cfg.bg,
        color: cfg.fg,
        borderRadius: 2,
        fontFamily: 'var(--font-sans)',
        fontWeight: 600,
      }}
    >
      {cfg.label}
    </span>
  );
}
